/**
 * 华奋达 GIF 灯效 Tab、高级键、V2 灯效模式隐藏 — 共用设备白名单（与 supportedDevices 一致）。
 * 有线与 2.4G 接收器 identifier 不同；M98 基础/屏幕版靠 product 区分。
 */
import { isHuafendaMagneticAxisEquipment } from "./hfdMagneticAxis";
import { isHuafendaDk108Equipment } from "./hfdDk108";
import { isHuafendaX87NeonEquipment } from "./hfdX87Neon";
import { isHuafendaX75GcEquipment } from "./hfdX75Gc";
import {
  isHfdFef9DongleX87NeonIdentifier,
  isHfdFef9DongleX87V2,
} from "./hfdFef9Dongle";
import { compareFirmwareVersions } from "./compareFirmwareVersions";

export const HFD_GIF_LIGHTING_DEVICE_IDENTIFIERS = new Set([
  "3141:32955:X87 V2",
  "3141:65273:2.4G Dongle",
  "3141:34818:X75GC",
  "3141:34819:X75GC",
  "3141:65273:X75GC",
  "3141:32946:X98",
  "3141:65274:2.4G Dongle",
  "3141:32774:MUSE-M98",
  "3141:10001:MUSE-M98",
  "3141:65278:MUSE-M98",
  "3141:65278:MUSE-M98:1",
  "3141:65278:MUSE-M98:5",
]);

export type HfdGifLightingEquipmentLike = {
  solutionRovider?: unknown;
  identifier?: unknown;
  product?: unknown;
  displayname?: unknown;
  productName?: unknown;
  keyboardType?: unknown;
  magneticAxis?: unknown;
};

/** M98 8K（displayname M98 (8K) / MUSE-M98-8K），无 GIF 灯效、灯带、心动时刻 */
export function isHuafendaM98_8KEquipment(
  equipment: HfdGifLightingEquipmentLike | null | undefined
): boolean {
  if (!equipment || typeof equipment !== "object") return false;
  if (isHuafendaX75GcEquipment(equipment)) return false;
  const kt = String(equipment.keyboardType ?? "").trim();
  if (kt === "M98 8K") return true;
  const dn = String(equipment.displayname ?? "").trim();
  if (dn === "M98 (8K)") return true;
  const pn = String(equipment.productName ?? "").trim();
  if (pn === "MUSE-M98-8K") return true;
  const id = String(equipment.identifier ?? "").trim();
  if (
    (id.includes("34819") && !id.toLowerCase().includes("x75gc")) ||
    id.includes("65276") ||
    id.includes("MUSE-M98-8K")
  ) {
    return true;
  }
  const pid = Number((equipment as { productId?: unknown }).productId);
  return pid === 0x8803 || pid === 0xfefc;
}

/** M98 屏幕版（M98TFT / displayname M98 Screen），仅该型号支持 GIF 灯效 */
export function isHuafendaM98ScreenEquipment(
  equipment: HfdGifLightingEquipmentLike | null | undefined
): boolean {
  if (isHuafendaM98_8KEquipment(equipment)) return false;
  if (!equipment || typeof equipment !== "object") return false;
  const kt = String(equipment.keyboardType ?? "").trim();
  if (kt === "M98TFT") return true;
  const dn = String(equipment.displayname ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (dn === "m98 screen") return true;
  const product = Number(equipment.product);
  if (product === 5) {
    const id = String(equipment.identifier ?? "").trim();
    if (id.includes("65278") && id.includes("MUSE-M98")) return true;
  }
  const id = String(equipment.identifier ?? "").trim();
  return id === "3141:10001:MUSE-M98" || id.startsWith("3141:10001:MUSE-M98:");
}

/** 华奋达 M98 系列（含 8K / 基础版 / 屏幕版）；高级键仅 M98 8K 开放 */
export function isHuafendaM98Equipment(
  equipment: HfdGifLightingEquipmentLike | null | undefined
): boolean {
  if (!equipment || typeof equipment !== "object") return false;
  if (isHuafendaM98_8KEquipment(equipment)) return true;
  if (isHuafendaBaseM98Equipment(equipment)) return true;
  if (isHuafendaM98ScreenEquipment(equipment)) return true;
  const kt = String(equipment.keyboardType ?? "").trim();
  if (kt === "M98" || kt === "M98TFT" || kt === "M98 8K") return true;
  const dn = String(equipment.displayname ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
  if (dn === "m98" || dn === "m98 screen") return true;
  const id = String(equipment.identifier ?? "").trim();
  return isMuseM98Identifier(id);
}

/** M98 基础版 / 屏幕版不展示高级键（M98 8K 与 X87 V2 同级开放） */
export function isHuafendaM98AdvancedKeyBlocked(
  equipment: HfdGifLightingEquipmentLike | null | undefined
): boolean {
  if (!equipment || typeof equipment !== "object") return false;
  if (isHuafendaM98_8KEquipment(equipment)) return false;
  return isHuafendaM98Equipment(equipment);
}

/** 普通 M98（非屏幕版），不支持 GIF 灯效 */
function isHuafendaBaseM98Equipment(
  equipment: HfdGifLightingEquipmentLike
): boolean {
  const kt = String(equipment.keyboardType ?? "").trim();
  if (kt === "M98") return true;
  const dn = String(equipment.displayname ?? "").trim();
  if (dn === "M98") return true;
  const product = Number(equipment.product);
  if (product === 1) {
    const id = String(equipment.identifier ?? "").trim();
    if (id.includes("65278") && id.includes("MUSE-M98")) return true;
  }
  const id = String(equipment.identifier ?? "").trim();
  return id === "3141:32774:MUSE-M98" || id.startsWith("3141:32774:MUSE-M98:");
}

function isMuseM98Identifier(id: string): boolean {
  return id.includes("MUSE-M98") || id.includes("32774:MUSE-M98");
}

export function resolveHfdDeviceLookupKeys(
  equipmentIdentifier: string,
  product: unknown
): string[] {
  const id = String(equipmentIdentifier ?? "").trim();
  if (!id) return [];
  const keys: string[] = [];
  if (
    product !== undefined &&
    product !== null &&
    String(product).trim() !== ""
  ) {
    keys.push(`${id}:${product}`);
  }
  keys.push(id);
  return keys;
}

function matchesIdentifierWhitelist(keys: string[]): boolean {
  for (const k of keys) {
    if (HFD_GIF_LIGHTING_DEVICE_IDENTIFIERS.has(k)) return true;
    // identifier 带 product 后缀时仍命中裸 id 白名单（如 3141:65273:2.4G Dongle:2:5）
    for (const bare of HFD_GIF_LIGHTING_DEVICE_IDENTIFIERS) {
      if (k === bare || k.startsWith(`${bare}:`)) return true;
    }
  }
  return false;
}

/** 华奋达「心动时刻」灯效 mode */
export const HFD_HEARTBEAT_LIGHTING_MODE = 254;

/** X87 V2 有线 identifier；2.4G 接收器 PID 65273（0xfef9）与 X75GC 共用，按名称区分 */
const HFD_X87_V2_WIRED_IDENTIFIER = "3141:32955:X87 V2";
const HFD_X87_V2_24G_ID_PREFIX = "3141:65273:";

/** 经典 X87 有线 / 2.4G identifier（非 V2） */
const HFD_X87_CLASSIC_WIRED_IDENTIFIER = "3141:32774:X87";
const HFD_X87_CLASSIC_24G_IDENTIFIER = "3141:65278:2.4G Dongle:2:5";

function isExactOrPrefixedId(id: string, bare: string): boolean {
  return id === bare || id.startsWith(`${bare}:`);
}

/** 是否为华奋达经典 X87（有线 / 2.4G，不含 V2 / Neon）；高级键仅开放 SOCD */
export function isHuafendaClassicX87Equipment(
  equipment: HfdGifLightingEquipmentLike | null | undefined
): boolean {
  if (!equipment || typeof equipment !== "object") return false;
  if (isHuafendaX87V2Equipment(equipment)) return false;
  if (isHuafendaX87NeonEquipment(equipment)) return false;

  const provider = String(equipment.solutionRovider ?? "")
    .trim()
    .toLowerCase();
  if (provider && provider !== "huafenda") return false;

  const id = String(equipment.identifier ?? "").trim();
  const dn = String(equipment.displayname ?? "").trim();
  const pn = String(equipment.productName ?? "").trim();

  if (dn === "X87" || pn === "X87") return true;
  if (isExactOrPrefixedId(id, HFD_X87_CLASSIC_WIRED_IDENTIFIER)) return true;
  if (isExactOrPrefixedId(id, HFD_X87_CLASSIC_24G_IDENTIFIER)) return true;

  return false;
}

/** 经典 X87 灯带 Tab：隐藏亮度/速度调节（不含 X87 V2 / X87 HE） */
export function shouldHideHuafendaClassicX87LightStripSpeedAndBrightness(
  equipment: HfdGifLightingEquipmentLike | null | undefined
): boolean {
  return isHuafendaClassicX87Equipment(equipment);
}

/** 与 boardConfig/huafenda/lighting supportsHfdLightBox 机型列表一致 */
const HFD_NO_LIGHT_BOX_DISPLAYNAMES = new Set([
  "battle68",
  "Tank68 HE",
  "X75 Pro",
  "X75 Pro Screen",
  "M98",
  "M98 (8K)",
  "M98 Screen",
  "DK108",
  "SMART875",
  "X98 (1K)",
  "X87 Neon",
]);

/** 华奋达灯箱/灯带（getLightBox）是否对该展示名开放 */
export function supportsHuafendaLightBoxByDisplayname(
  displayname?: string | null
): boolean {
  const name = String(displayname ?? "").trim();
  return name.length > 0 && !HFD_NO_LIGHT_BOX_DISPLAYNAMES.has(name);
}

/**
 * 灯带 Tab 展示虚拟灯带（隐藏虚拟键盘）：X87 / X87 V2 等支持灯箱的华奋达机型。
 * 容园 X87S 等仍走「灯带 Tab 只显键盘」策略，不在此函数范围内。
 */
export function shouldShowHuafendaLightStripVisualPreview(
  equipment: HfdGifLightingEquipmentLike | null | undefined
): boolean {
  if (!equipment || typeof equipment !== "object") return false;
  const provider = String(equipment.solutionRovider ?? "")
    .trim()
    .toLowerCase();
  if (provider && provider !== "huafenda") return false;
  return supportsHuafendaLightBoxByDisplayname(
    String(equipment.displayname ?? equipment.productName ?? "").trim()
  );
}

/** 华奋达设备是否仅支持 SOCD 高级键（经典 X87；读写走与 X87 V2 相同的 HFD SOCD 链路） */
export function isHfdSocdOnlyAdvancedKeyboard(
  equipment: HfdGifLightingEquipmentLike | null | undefined
): boolean {
  return isHuafendaClassicX87Equipment(equipment);
}

/** 经典 X87 高级键所需最低固件版本（appVersion） */
export const HFD_CLASSIC_X87_ADVANCED_KEY_MIN_FIRMWARE = "1.23";

/** 已知固件版本低于门槛时返回 true；版本未知或为空时不拦截 */
export function isHfdClassicX87AdvancedKeyFirmwareBlocked(
  firmwareVersion: string | null | undefined
): boolean {
  const core = String(firmwareVersion ?? "").trim();
  if (!core) return false;
  return (
    compareFirmwareVersions(core, HFD_CLASSIC_X87_ADVANCED_KEY_MIN_FIRMWARE) < 0
  );
}

/** 是否为华奋达 X87 V2（有线 / 2.4G），用于隐藏不支持的心动时刻灯效 */
export function isHuafendaX87V2Equipment(
  equipment: HfdGifLightingEquipmentLike | null | undefined
): boolean {
  if (!equipment || typeof equipment !== "object") return false;
  const provider = String(equipment.solutionRovider ?? "")
    .trim()
    .toLowerCase();
  if (provider && provider !== "huafenda") return false;

  const id = String(equipment.identifier ?? "").trim();
  const dn = String(equipment.displayname ?? "").trim();
  const pn = String(equipment.productName ?? "").trim();
  const nameBlob = `${dn} ${pn}`;

  // 与 Neon 同接收器：仅排除全量 id `3141:65273:2.4G Dongle:3:10`
  if (isHfdFef9DongleX87NeonIdentifier(id)) return false;
  if (
    /\bx87\s*neon\b/i.test(nameBlob) &&
    !id.startsWith(HFD_X87_V2_24G_ID_PREFIX)
  ) {
    return false;
  }
  if (/\bx87\s*v2\b/i.test(nameBlob)) return true;
  if (id.includes("X87 V2")) return true;
  if (
    id.startsWith(HFD_X87_V2_24G_ID_PREFIX) &&
    !id.includes("X75GC") &&
    !isHfdFef9DongleX87NeonIdentifier(id)
  ) {
    return true;
  }
  if (
    id === HFD_X87_V2_WIRED_IDENTIFIER ||
    id.startsWith(`${HFD_X87_V2_WIRED_IDENTIFIER}:`)
  ) {
    return true;
  }
  if (id.includes("32955") && /x87\s*v2/i.test(id)) return true;

  const pid = Number((equipment as { productId?: unknown }).productId);
  if (pid === 0x80bb) return true;
  if (pid === 0xfef9) return isHfdFef9DongleX87V2(equipment);

  return false;
}

/** X75 Pro 有线 identifier（不含屏幕版 32777） */
const HFD_X75_PRO_WIRED_IDENTIFIERS = [
  "3141:32774:X75 Pro",
  "3141:32978:EWEADN-X75Pro",
] as const;

/** 是否为华奋达 X75 Pro（有线 / 2.4G，不含屏幕版），用于隐藏不支持的心动时刻灯效 */
export function isHuafendaX75ProEquipment(
  equipment: HfdGifLightingEquipmentLike | null | undefined
): boolean {
  if (!equipment || typeof equipment !== "object") return false;
  const provider = String(equipment.solutionRovider ?? "")
    .trim()
    .toLowerCase();
  if (provider && provider !== "huafenda") return false;

  const kt = String(equipment.keyboardType ?? "").trim();
  if (kt === "75ProTFT") return false;
  if (kt === "75Pro") return true;

  const dn = String(equipment.displayname ?? "").trim();
  const pn = String(equipment.productName ?? "").trim();
  const nameBlob = `${dn} ${pn}`;
  if (/screen|tft/i.test(nameBlob)) return false;
  if (/x75\s*-?\s*pro/i.test(nameBlob) || /eweadn-x75pro/i.test(nameBlob)) {
    return true;
  }

  const id = String(equipment.identifier ?? "").trim();
  for (const wired of HFD_X75_PRO_WIRED_IDENTIFIERS) {
    if (id === wired || id.startsWith(`${wired}:`)) return true;
  }
  if (id.includes("EWEADN-X75Pro")) return true;

  const pid = Number((equipment as { productId?: unknown }).productId);
  // 0x80d2 为 EWEADN-X75Pro 专属；0x8006 与 M98 共用，须结合名称/identifier
  if (pid === 0x80d2) return true;

  return false;
}

/** 需隐藏「心动时刻」(mode=254) 的设备：X87 V2、X87 Neon、DK108、M98 8K、M98 屏幕版、X75 Pro、磁轴（battle68 等） */
export function shouldHideHfdHeartbeatLighting(
  equipment: HfdGifLightingEquipmentLike | null | undefined
): boolean {
  return (
    isHuafendaX87V2Equipment(equipment) ||
    isHuafendaX87NeonEquipment(equipment) ||
    isHuafendaX75GcEquipment(equipment) ||
    isHuafendaDk108Equipment(equipment) ||
    isHuafendaM98_8KEquipment(equipment) ||
    isHuafendaM98ScreenEquipment(equipment) ||
    isHuafendaX75ProEquipment(equipment) ||
    isHuafendaMagneticAxisEquipment({
      ...equipment,
      solutionRovider: equipment?.solutionRovider ?? "huafenda",
    })
  );
}

function matchesDisplaynameFallback(
  equipment: HfdGifLightingEquipmentLike
): boolean {
  const dn = String(equipment.displayname ?? "").trim();
  const pn = String(equipment.productName ?? "").trim();
  const id = String(equipment.identifier ?? "").trim();

  if (id.includes("X87 V2") || dn === "X87 V2" || pn === "X87 V2") return true;
  if (id.includes("X75GC") || dn === "X75GC" || pn === "X75GC") return true;
  if (isHuafendaM98ScreenEquipment(equipment)) return true;
  if (id.includes("X98") || dn === "X98 (1K)" || pn === "X98") return true;

  return false;
}

/** 是否展示 GIF 灯效 Tab（及同类 V2 华奋达能力） */
export function isHfdGifLightingDeviceAllowed(
  equipment: HfdGifLightingEquipmentLike | null | undefined
): boolean {
  if (!equipment || typeof equipment !== "object") return false;
  if (
    String(equipment.solutionRovider ?? "")
      .trim()
      .toLowerCase() !== "huafenda"
  ) {
    return false;
  }
  if (isHuafendaBaseM98Equipment(equipment)) return false;
  if (isHuafendaDk108Equipment(equipment)) return false;
  if (isHuafendaM98_8KEquipment(equipment)) return false;
  if (isHuafendaX87NeonEquipment(equipment)) return true;

  const id = String(equipment.identifier ?? "").trim();
  const keys = resolveHfdDeviceLookupKeys(id, equipment.product);
  if (keys.length > 0 && matchesIdentifierWhitelist(keys)) {
    if (isMuseM98Identifier(id) && !isHuafendaM98ScreenEquipment(equipment)) {
      return false;
    }
    return true;
  }
  return matchesDisplaynameFallback(equipment);
}

/** SDK 写入 product 后 hfdId 带后缀；白名单多为裸 identifier（X87 V2 等） */
export function isHfdDeviceInIdentifierSet(
  equipmentIdentifier: string,
  product: unknown,
  extras?: Pick<HfdGifLightingEquipmentLike, "displayname" | "productName">
): boolean {
  return isHfdGifLightingDeviceAllowed({
    solutionRovider: "huafenda",
    identifier: equipmentIdentifier,
    product,
    ...extras,
  });
}

/** 华奋达高级键菜单：hfdId 与裸 identifier 双路径匹配；磁轴机型（如 battle68）一律开放 */
export function isHfdAdvancedKeyboardAllowed(
  hfdId: string,
  identifier: string,
  extras?: Pick<HfdGifLightingEquipmentLike, "displayname" | "productName"> & {
    keyboardType?: unknown;
    magneticAxis?: unknown;
  }
): boolean {
  const m98Ctx = {
    solutionRovider: "huafenda",
    identifier: hfdId || identifier,
    ...extras,
  };
  // M98 8K：与 X87 V2 同级，支持 SOCD / MT / TGL / CB
  if (isHuafendaM98_8KEquipment(m98Ctx)) return true;
  if (
    identifier &&
    identifier !== m98Ctx.identifier &&
    isHuafendaM98_8KEquipment({ ...m98Ctx, identifier })
  ) {
    return true;
  }
  // X87 Neon：与 X87 V2 同级，支持 SOCD / MT / TGL / CB；GIF 灯效由 isHfdGifLightingDeviceAllowed 单独放开
  if (isHuafendaX87NeonEquipment(m98Ctx)) return true;
  if (
    identifier &&
    identifier !== m98Ctx.identifier &&
    isHuafendaX87NeonEquipment({ ...m98Ctx, identifier })
  ) {
    return true;
  }
  // M98 基础版 / 屏幕版不展示高级键
  if (isHuafendaM98AdvancedKeyBlocked(m98Ctx)) return false;
  if (
    identifier &&
    identifier !== m98Ctx.identifier &&
    isHuafendaM98AdvancedKeyBlocked({ ...m98Ctx, identifier })
  ) {
    return false;
  }
  // DK108 不支持高级键
  if (isHuafendaDk108Equipment(m98Ctx)) return false;
  if (
    identifier &&
    identifier !== m98Ctx.identifier &&
    isHuafendaDk108Equipment({ ...m98Ctx, identifier })
  ) {
    return false;
  }

  // 经典 X87：方案商已支持高级键（仅 SOCD；模式列表由页面侧过滤）
  // 须先按名称/固定 id 判定，避免同 PID 时 hfdId 误命中 M98 等白名单或被整入口关掉
  if (
    isHuafendaMagneticAxisEquipment({
      solutionRovider: "huafenda",
      identifier: hfdId || identifier,
      displayname: extras?.displayname,
      productName: extras?.productName,
      keyboardType: extras?.keyboardType,
      magneticAxis: extras?.magneticAxis,
    })
  ) {
    return true;
  }
  // 经典 X87 不支持高级键（同 PID 时 hfdId 可能误命中 M98 等白名单，须先按名称/固定 id 排除）
  const classicEq = {
    solutionRovider: "huafenda",
    identifier: hfdId || identifier,
    ...extras,
  };
  if (isHuafendaClassicX87Equipment(classicEq)) return true;
  if (
    identifier &&
    isHuafendaClassicX87Equipment({
      solutionRovider: "huafenda",
      identifier,
      ...extras,
    })
  ) {
    return true;
  }

  if (HFD_GIF_LIGHTING_DEVICE_IDENTIFIERS.has(hfdId)) return true;
  if (identifier && HFD_GIF_LIGHTING_DEVICE_IDENTIFIERS.has(identifier)) {
    return true;
  }
  if (
    isHfdGifLightingDeviceAllowed({
      solutionRovider: "huafenda",
      identifier: hfdId,
      ...extras,
    })
  ) {
    return true;
  }
  if (
    identifier &&
    isHfdGifLightingDeviceAllowed({
      solutionRovider: "huafenda",
      identifier,
      ...extras,
    })
  ) {
    return true;
  }
  return false;
}
